/**
 * useTrack Hook
 * 职责：函数式埋点API，支持节流配置
 */

import { useCallback, useRef } from "react";
import { trackerV2, type TrackOptions } from "../core/TrackerV2";

export interface UseTrackOptions extends TrackOptions {
  throttle?: number; // 节流时间（毫秒）
}

export function useTrack(eventName: string, options: UseTrackOptions = {}) {
  const throttleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTrackTime = useRef<number>(0);
  const { throttle, ...trackOptions } = options;

  return useCallback(
    (data: Record<string, any> = {}, additionalOptions: TrackOptions = {}) => {
      // 节流处理
      const now = Date.now();
      const throttleTime = throttle || 0;

      if (throttleTime > 0 && now - lastTrackTime.current < throttleTime) {
        // 清除之前的定时器
        if (throttleTimer.current) {
          clearTimeout(throttleTimer.current);
        }

        // 设置新的定时器
        throttleTimer.current = setTimeout(() => {
          trackerV2.track(eventName, data, {
            ...trackOptions,
            ...additionalOptions,
          });
          lastTrackTime.current = Date.now();
        }, throttleTime - (now - lastTrackTime.current));

        return;
      }

      // 直接执行
      trackerV2.track(eventName, data, {
        ...trackOptions,
        ...additionalOptions,
      });
      lastTrackTime.current = now;
    },
    [eventName, throttle, trackOptions]
  );
}

/**
 * useTrackCallback Hook
 * 返回一个可以直接作为事件处理器的回调函数
 */
export function useTrackCallback(
  eventName: string,
  getData: () => Record<string, any>,
  options: UseTrackOptions = {}
) {
  const track = useTrack(eventName, options);

  return useCallback(
    (e?: React.MouseEvent | React.SyntheticEvent) => {
      const data = getData();
      const targetNode = (e?.currentTarget || e?.target) as Element | undefined;

      track(data, { targetNode });
    },
    [track, getData]
  );
}

/**
 * useTrackEffect Hook
 * 在依赖变化时自动上报埋点
 */
export function useTrackEffect(
  eventName: string,
  data: Record<string, any>,
  deps: React.DependencyList,
  options: TrackOptions = {}
) {
  const { useEffect } = require("react");

  useEffect(() => {
    trackerV2.track(eventName, data, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
